import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  IPaginatedResponse,
  IUser,
  IUsersSearchQuery,
  SortOrder,
  UserSortField,
} from '@manage/shared-types';
import { isPlatformAdmin, MIN_TEXT_FILTER_LENGTH } from '@manage/shared-types';
import { apiClient } from '@/services/api';
import { extractErrorMessage } from '@/utils/apiError';
import { useAuthStore } from '@/stores/auth';

export interface UsersFilters {
  id: number | null;
  name: string;
  email: string;
  accountName: string;
  accountId: number | null;
}

type PersistedState = Required<Pick<IUsersSearchQuery, 'page' | 'pageSize'>> &
  Pick<IUsersSearchQuery, 'sortField' | 'sortOrder'> & {
    filters: UsersFilters;
  };

const defaultState: PersistedState = {
  page: 1,
  pageSize: 25,
  sortField: 'createdAt',
  sortOrder: -1,
  filters: {
    id: null,
    name: '',
    email: '',
    accountName: '',
    accountId: null,
  },
};

const loadPersistedState = (storageKey: string): { state: PersistedState } => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return { state: { ...defaultState } };
    }

    const parsed = JSON.parse(raw) as Partial<PersistedState>;

    return {
      state: {
        ...defaultState,
        ...parsed,
        page: parsed.page && parsed.page > 0 ? parsed.page : defaultState.page,
        pageSize: parsed.pageSize && parsed.pageSize > 0 ? parsed.pageSize : defaultState.pageSize,
      },
    };
  } catch (error) {
    console.warn('Failed to parse persisted users state, using defaults:', error);

    return { state: { ...defaultState } };
  }
};

// Re-export for use in components
export { MIN_TEXT_FILTER_LENGTH };

export const useUsersStore = defineStore('users', () => {
  const authStore = useAuthStore();
  const STORAGE_KEY = computed(() => `manage.users.${authStore.user?.id ?? 'unknown'}`);

  const users = ref<IUser[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const error = ref<string>('');
  /**
   * True when platform admin has no active filters applied.
   * When true, we skip the API call and show a prompt to apply filters.
   */
  const filterPromptVisible = ref(false);

  // Initialize with defaults; will be updated when user authenticates
  const page = ref<number>(defaultState.page);
  const pageSize = ref<number>(defaultState.pageSize);
  const sortField = ref<UserSortField | undefined>(defaultState.sortField);
  const sortOrder = ref<SortOrder | undefined>(defaultState.sortOrder);
  const filters = ref<UsersFilters>({ ...defaultState.filters });

  // Track whether we've loaded persisted state for the current user
  const persistedStateLoaded = ref(false);

  // Load persisted state when user becomes available (after auth completes)
  watch(
    () => authStore.user?.id,
    (userId) => {
      if (userId && !persistedStateLoaded.value) {
        const { state: persisted } = loadPersistedState(STORAGE_KEY.value);

        filters.value = { ...persisted.filters };
        page.value = persisted.page;
        pageSize.value = persisted.pageSize;
        sortField.value = persisted.sortField;
        sortOrder.value = persisted.sortOrder;
        persistedStateLoaded.value = true;
      }
    },
    { immediate: true }
  );

  const query = computed<IUsersSearchQuery>(() => ({
    id: typeof filters.value.id === 'number' ? filters.value.id : undefined,
    page: page.value,
    pageSize: pageSize.value,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
    name: filters.value.name.trim() ? filters.value.name.trim() : undefined,
    email: filters.value.email.trim() ? filters.value.email.trim() : undefined,
    accountName: filters.value.accountName.trim() ? filters.value.accountName.trim() : undefined,
    accountId: typeof filters.value.accountId === 'number' ? filters.value.accountId : undefined,
  }));

  const persist = () => {
    const state: PersistedState = {
      page: page.value,
      pageSize: pageSize.value,
      sortField: sortField.value ?? defaultState.sortField,
      sortOrder: sortOrder.value ?? defaultState.sortOrder,
      filters: {
        id: filters.value.id,
        name: filters.value.name,
        email: filters.value.email,
        accountName: filters.value.accountName,
        accountId: filters.value.accountId,
      },
    };

    sessionStorage.setItem(STORAGE_KEY.value, JSON.stringify(state));
  };

  watch([page, pageSize, sortField, sortOrder], persist, { deep: false });
  watch(filters, persist, { deep: true });

  /**
   * Check if any filter has a meaningful value that would trigger an API search.
   * Text filters must meet MIN_TEXT_FILTER_LENGTH to count as active.
   */
  const hasActiveFilters = (): boolean => {
    const f = filters.value;

    return !!(
      f.id ||
      f.accountId ||
      (f.name && f.name.trim().length >= MIN_TEXT_FILTER_LENGTH) ||
      (f.email && f.email.trim().length >= MIN_TEXT_FILTER_LENGTH) ||
      (f.accountName && f.accountName.trim().length >= MIN_TEXT_FILTER_LENGTH)
    );
  };

  const fetchUsers = async () => {
    // Guard: ensure user is authenticated before fetching
    if (!authStore.user) {
      console.warn('[users.ts] fetchUsers called without authenticated user');

      return;
    }

    // Platform admins MUST have active filters to see any data.
    // Non-platform admins (AWA admins) are account-scoped and proceed without this restriction.
    if (userIsPlatformAdmin.value && !hasActiveFilters()) {
      users.value = [];
      total.value = 0;
      filterPromptVisible.value = true;
      loading.value = false;
      error.value = '';

      return;
    }

    filterPromptVisible.value = false;
    loading.value = true;
    error.value = '';

    try {
      const response = await apiClient.get<IPaginatedResponse<IUser>>('/users/search', {
        params: query.value,
      });

      users.value = response.data.data;
      total.value = response.data.meta.total;
    } catch (e) {
      error.value = extractErrorMessage(e, 'Failed to load users');
    } finally {
      loading.value = false;
    }
  };

  const setPage = (nextPage: number) => {
    page.value = nextPage;
  };

  const setPageSize = (nextPageSize: number) => {
    pageSize.value = nextPageSize;
  };

  const setSort = (
    nextSortField: UserSortField | undefined,
    nextSortOrder: SortOrder | undefined
  ) => {
    sortField.value = nextSortField;
    sortOrder.value = nextSortOrder;
  };

  const clearAllFilters = () => {
    filters.value = { ...defaultState.filters };
    page.value = 1;
  };

  const firstRowIndex = computed(() => (page.value - 1) * pageSize.value);

  /**
   * Whether the current user is a platform admin.
   * Returns false if user is not authenticated (null/undefined).
   */
  const userIsPlatformAdmin = computed(() =>
    authStore.user ? isPlatformAdmin(authStore.user) : false
  );

  return {
    users,
    total,
    loading,
    error,
    filterPromptVisible,
    page,
    pageSize,
    sortField,
    sortOrder,
    filters,
    query,
    firstRowIndex,
    fetchUsers,
    setPage,
    setPageSize,
    setSort,
    clearAllFilters,
  };
});
